import {Box, IconButton, Tooltip, Typography} from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  IBaseTerm,
  ICategory,
  IGlossary,
  ITerm,
  OrderDirection,
} from "@evennt-tech/api-v2-core";
import {apiV2} from "api/firebase";
import {encodeBase64Payload} from "tools/common/base64";
import GlossaryCategoryModal from "view/apps/Organiser/pages/Show/Glossary/GlossaryCategoryModal";
import AlphabeticFilter from "view/components/AlphabeticFilter";
import {orderBy} from "lodash";
import GlossaryTermFormModal from "view/apps/Organiser/pages/Show/Glossary/GlossaryTermFormModal";
import {
  releaseUploadedImages,
  showImageUploadScope,
  uploadRichTextImages,
} from "tools/richText/uploadRichTextImages";
import {useCurrentShowId} from "hooks";
import BackdropLoader from "view/components/BackdropLoader";
import {UserGlossaryRes} from "hooks/show/useGlossary";
import ConfirmModal from "view/components/ConfirmModal";
import ShowInfoDuplicateWarningModal from "view/components/ShowInfoDuplicateWarningModal";
import {TranslationConflictModal} from "view/components/Localisation";
import useShowInfoLibraryActions from "hooks/show/glossary/useShowInfoLibraryActions";
import {useTranslationLanguages} from "hooks/translation";
import {isLinkedCategory} from "api/showInfoLibrary/types";
import {useGlossarySearchQuery} from "store/glossary/glossary.store.selectors";
import {useGlossaries} from "store/glossary/glossary.store";
import clsx from "clsx";
import HideAllButton from "./components/HideAllButton";
import GlossaryTermItem from "./components/GlossaryTermItem";
import useStyles from "./styles";
import allCategory from "./allCategory";
import canOpenSectionForm from "./canOpenSectionForm";

type EditedTerm = IBaseTerm & {id: string; buildTypes: string[]};

type GlossaryTermsProps = {
  showId: string;
  showGlossary: UserGlossaryRes;
  glossary: IGlossary;
  terms: ITerm[];
  categories: ICategory[];
  selectedCategory?: ICategory | null;
  searchValue: string;
  readonly?: boolean;
  termsListRef: React.MutableRefObject<HTMLDivElement | null>;
  setSelectedCategory: (category: ICategory | null) => void;
  setScrollToTermId: (termId: string) => void;
  scrollToTerm: () => void;
  selectedTerm: (category: ICategory, termId: string) => void;
  onSetPopupTermId: (termId: string) => void;
  isPrintView?: boolean;
};

const termsListId = "terms-list";

const GlossaryTerms: React.FC<GlossaryTermsProps> = ({
  showId,
  showGlossary,
  glossary,
  terms,
  categories,
  selectedCategory,
  termsListRef,
  setSelectedCategory,
  setScrollToTermId,
  readonly,
  scrollToTerm,
  selectedTerm,
  onSetPopupTermId,
  isPrintView,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const currentShowId = useCurrentShowId();
  const searchQuery = useGlossarySearchQuery();
  const fetchCategories = useGlossaries((state) => state.fetchCategories);
  const fetchTerms = useGlossaries((state) => state.fetchTerms);

  const classes = useStyles();

  const isUpdatingTermOrderRef = useRef(false);

  const libraryActions = useShowInfoLibraryActions(showId);
  const {languages: showLanguages} = useTranslationLanguages(showId);
  const [categoryToUnlink, setCategoryToUnlink] = useState<ICategory | null>(
    null
  );

  const [termToEdit, setTermToEdit] = useState<ITerm>();
  const [isEditCategoryModalOpen, setCategoryModalOpen] = useState(false);
  const [isLoadedAllImgs, setIsLoadedAllImgs] = useState(false);
  const [
    editedTermToConfirm,
    setEditedTermToConfirm,
  ] = useState<EditedTerm | null>(null);
  const [isUpdatingCategoryOrder, setIsUpdatingCategoryOrder] = useState(false);
  const hasScrolledToTerm = useRef(false);

  const imageCount = useMemo(() => {
    if (!terms?.length) return 0;

    return terms.reduce((acc, term) => {
      const parsedDoc = new DOMParser().parseFromString(
        term.description,
        "text/html"
      );

      const imagesLen = parsedDoc.images.length;
      return acc + imagesLen;
    }, 0);
  }, [terms]);

  useEffect(() => {
    const allLinks = document.querySelectorAll("a");
    allLinks.forEach((link) => {
      if (link.href.indexOf("termId") > 0) {
        const url = new URL(link.href);
        const termId = url.searchParams.get("termId");
        const categoryId = url.searchParams?.get("category");
        if (!termId || !categoryId) {
          return;
        }
        link.setAttribute("rel", "");
        link.setAttribute("target", "");
        const clickHandler = (e: MouseEvent) => {
          e.preventDefault();

          const category = categories.find((cat) => cat.id === categoryId);

          if (!category || !termId) {
            return;
          }

          selectedTerm(category, termId);
        };
        link.addEventListener("click", clickHandler);
      }
    });
  }, [categories, setScrollToTermId, setSelectedCategory]);

  useEffect(() => {
    const container = contentRef.current;
    if (!terms.length || !container || hasScrolledToTerm.current) return;

    let resizeObserver: ResizeObserver;
    let timeout: NodeJS.Timeout;

    const safeScroll = () => {
      hasScrolledToTerm.current = true;
      setIsLoadedAllImgs(false);

      resizeObserver = new ResizeObserver(() => {
        clearTimeout(timeout);

        // NOTE: wait til dangerouslySetInnerHtml mounted in DOM
        timeout = setTimeout(() => {
          scrollToTerm();
          resizeObserver.disconnect();
        }, 500);
      });

      resizeObserver.observe(container);
    };

    if (!imageCount) {
      safeScroll();
      return;
    }

    let observer: MutationObserver;
    const checkImagesAndScroll = () => {
      const images = container.querySelectorAll<HTMLImageElement>("img");

      if (images.length < imageCount) {
        return;
      }

      observer.disconnect();

      setIsLoadedAllImgs(true);
      let loadedCount = 0;

      const handleImageDone = () => {
        loadedCount += 1;
        if (loadedCount === images.length) {
          safeScroll();
        }
      };

      images.forEach((img) => {
        if (img.complete) {
          handleImageDone();
        } else {
          img.onload = handleImageDone;
          img.onerror = handleImageDone;
        }
      });
    };

    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes.length) {
          checkImagesAndScroll();
        }
      }
    });

    observer.observe(container, {childList: true, subtree: true});

    checkImagesAndScroll();

    return () => {
      if (observer) observer.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      clearTimeout(timeout);
      const images = container.querySelectorAll<HTMLImageElement>("img");
      images.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [terms, imageCount]);

  const removeTerm = async (term: ITerm) => {
    await apiV2.glossary.category.term.delete({
      termId: term.id,
      showId,
    });
  };

  const pinTerm = async (term: ITerm) => {
    // Rewrite any `blob:` image srcs in the description to storage URLs and
    // collect their second-leg tokens before the edit call — the helper
    // throws (aborting the save) on any upload failure.
    const {html: description, tokens, blobSrcs} = await uploadRichTextImages(
      term.description,
      {
        initFileUpload: showImageUploadScope(showId),
      }
    );

    await apiV2.glossary.category.term.edit({
      showId,
      termId: term.id,
      pinned: !term.pinned,
      categoryId: term.categoryId,
      title: term.title,
      builderIds: term?.termToBuilders?.map(({builderId}) => builderId) || [],
      // base64-wrapped against WAF false-positives — see `Glossary/index.tsx`.
      description: encodeBase64Payload(description) ?? description,
      ...(tokens.length && {richTextFileTokens: tokens}),
    });

    // Only release the blob registry entries after the save actually
    // succeeded — a thrown error above leaves them intact.
    releaseUploadedImages(blobSrcs);

    await fetchTerms(showId, true);
  };

  const popupTerm = async (term: ITerm) => onSetPopupTermId(term.id);

  const removeCategory = async () => {
    const confirmText =
      "Are you sure you want to delete this category with all sections in it?";
    if (confirm(confirmText)) {
      const category = categories?.find(
        (category) => category.id === selectedCategory?.id
      );
      if (!category) {
        return;
      }

      await apiV2.glossary.category.delete({
        categoryId: category.id,
        showId,
      });

      setSelectedCategory(null);
    }
  };

  const moveTerm = async (term: ITerm, direction: OrderDirection) => {
    if (isUpdatingTermOrderRef.current) return;

    try {
      isUpdatingTermOrderRef.current = true;
      await apiV2.glossary.category.term.changeOrder({
        termId: term.id,
        showId,
        direction,

        ...(selectedCategory &&
          selectedCategory.id !== "all" && {
            categoryId: selectedCategory.id,
          }),
      });
      await fetchTerms(showId, true);
    } catch (e) {
      console.error("Error occurred changing term order: ", e);
    } finally {
      isUpdatingTermOrderRef.current = false;
    }
  };

  const createGlossaryTermActions = (term: ITerm) => {
    if (readonly) {
      return undefined;
    }

    const canOpenForm = canOpenSectionForm(term, libraryActions);

    return {
      onMoveUp: (term: ITerm) => moveTerm(term, OrderDirection.UP),
      onMoveDown: (term: ITerm) => moveTerm(term, OrderDirection.DOWN),
      ...(canOpenForm && {onEdit: () => setTermToEdit(term)}),
      onRemove: () => removeTerm(term),
      onPin: () => pinTerm(term),
      onPopup: () => popupTerm(term),
    };
  };

  const onSaveTerm = async (edited: EditedTerm) => {
    // Rewrite any `blob:` image srcs in the description to storage URLs and
    // collect their second-leg tokens before the edit call — the helper
    // throws (aborting the save) on any upload failure.
    const {html: description, tokens, blobSrcs} = await uploadRichTextImages(
      edited.description,
      {
        initFileUpload: showImageUploadScope(showId),
      }
    );

    await apiV2.glossary.category.term.edit({
      termId: edited.id,
      pinned: edited?.pinned || false,
      categoryId: edited.categoryId,
      title: edited.title,
      // base64-wrapped against WAF false-positives — see `Glossary/index.tsx`.
      description: encodeBase64Payload(description) ?? description,
      builderIds: edited.buildTypes,
      showId,
      ...(tokens.length && {richTextFileTokens: tokens}),
    });

    // Only release the blob registry entries after the save actually
    // succeeded — a thrown error above leaves them intact.
    releaseUploadedImages(blobSrcs);

    // The list otherwise refreshes only when the UPDATED_TERM_GLOSSARY
    // notification arrives; reopening the section before that round-trip
    // (or with the socket not yet subscribed) would seed the form from the
    // pre-save term, so re-read before the modal closes.
    await fetchTerms(showId, true);

    setTermToEdit(undefined);
  };

  const onTryToSaveTerm = async (edited: EditedTerm) => {
    const initialBuildersWithPopupEnabled = termToEdit?.termToBuilders?.filter(
      (ttb) => ttb.shouldShowPopup
    );
    if (initialBuildersWithPopupEnabled?.length) {
      const isNoneLeftWithPopupEnabled = initialBuildersWithPopupEnabled.every(
        (ttb) => !edited.buildTypes?.some((bId) => bId === ttb.builderId)
      );
      if (isNoneLeftWithPopupEnabled) {
        return setEditedTermToConfirm(edited);
      }
    }

    await onSaveTerm(edited);
  };

  const onConfirm = async () => {
    if (!editedTermToConfirm) {
      return;
    }

    await onSaveTerm(editedTermToConfirm);
    setEditedTermToConfirm(null);
  };

  const moveCategory = async (direction: OrderDirection) => {
    if (!selectedCategory || !currentShowId || isUpdatingCategoryOrder) return;

    try {
      setIsUpdatingCategoryOrder(true);
      await apiV2.glossary.category.changeOrder({
        showId: currentShowId,
        categoryId: selectedCategory.id,
        direction,
      });

      await fetchCategories(currentShowId, true);
    } catch (e) {
      console.error("Error occurred changing category order: ", e);
    } finally {
      setIsUpdatingCategoryOrder(false);
    }
  };

  const isAllSelected =
    selectedCategory === null || selectedCategory?.id === allCategory.id;

  const onCategoryModalClose = () => {
    setCategoryModalOpen(false);
  };

  const showCategoryButtons = !isAllSelected && !readonly;
  const showAllButton = isAllSelected && !readonly;

  const getTitle = () => {
    if (isAllSelected) {
      return allCategory.title;
    }
    if (!selectedCategory) {
      return null;
    }
    if (!searchQuery) {
      return selectedCategory.title;
    }
    return `${selectedCategory.title} (${terms.length})`;
  };

  return (
    <>
      {editedTermToConfirm && (
        <ConfirmModal
          isCalledByAnotherModal
          title="Confirm"
          onClose={() => setEditedTermToConfirm(null)}
          onSubmit={onConfirm}
          severity="warning"
          description={
            <>
              There is a Pop-up enabled for this section. Removing all build
              types will disable it.
              <br />
              <br />
              Are you sure you want to continue?
            </>
          }
        />
      )}
      {libraryActions.translationConflicts && libraryActions.pushingTerm && (
        <TranslationConflictModal
          conflicts={libraryActions.translationConflicts}
          languages={showLanguages}
          fieldLabels={{title: "Category title"}}
          currentShowId={showId}
          onClose={libraryActions.cancelPush}
          onMerge={libraryActions.resolveTranslationConflicts}
        />
      )}
      {libraryActions.pushMatches && libraryActions.pushingTerm && (
        <ShowInfoDuplicateWarningModal
          open
          sectionTitle={libraryActions.pushingTerm.title}
          matches={libraryActions.pushMatches}
          onClose={libraryActions.cancelPush}
          onConfirm={libraryActions.resolvePush}
        />
      )}
      {categoryToUnlink && (
        /*
          A section unlink needs no confirmation — nothing is lost, the inherited
          values are already on the row. A category does, because the result is
          counter-intuitive: only its *title* goes local, and every section inside
          it keeps inheriting.
        */
        <ConfirmModal
          isCalledByAnotherModal
          severity="warning"
          title="Unlink category from library?"
          description={`"${categoryToUnlink.title}" will become a local category on this Show and its name will no longer update from the library. The sections inside it stay linked and keep inheriting their content.`}
          submitTitle="Unlink"
          onClose={() => setCategoryToUnlink(null)}
          onSubmit={async () => {
            await libraryActions.unlinkCategory(categoryToUnlink);
            setCategoryToUnlink(null);
          }}
        />
      )}
      <div id={termsListId} className={classes.termsList} ref={termsListRef}>
        <BackdropLoader isLoading={isLoadedAllImgs} />
        <Box className={classes.categoryToolbar}>
          <Typography variant="h2">{getTitle()}</Typography>
          {showAllButton && <HideAllButton glossary={glossary} />}
          {showCategoryButtons && (
            <Box className={classes.categoryToobarActions}>
              {selectedCategory &&
                selectedCategory?.id !==
                  categories[categories.length - 1]?.id && (
                  <IconButton
                    disabled={isUpdatingCategoryOrder}
                    onClick={() => {
                      moveCategory(OrderDirection.DOWN);
                    }}
                  >
                    <ArrowDownwardIcon />
                  </IconButton>
                )}
              {selectedCategory && selectedCategory?.id !== categories[0]?.id && (
                <IconButton
                  disabled={isUpdatingCategoryOrder}
                  onClick={() => {
                    moveCategory(OrderDirection.UP);
                  }}
                >
                  <ArrowUpwardIcon />
                </IconButton>
              )}

              {selectedCategory && selectedCategory.id !== "all" && (
                <>
                  <IconButton>
                    <EditIcon
                      onClick={() => {
                        setCategoryModalOpen(true);
                      }}
                    />
                  </IconButton>
                  {/*
                    Unlinking a category is offered here rather than on the edit
                    form because it is an action on the category itself, and the
                    form's title field is simply locked while the link holds.
                  */}
                  {isLinkedCategory(selectedCategory) &&
                    libraryActions.isCompanyAdmin && (
                      <Tooltip title="Unlink category from library">
                        <IconButton
                          onClick={() => setCategoryToUnlink(selectedCategory)}
                        >
                          <LinkOffIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  <IconButton>
                    <DeleteIcon onClick={removeCategory} />
                  </IconButton>
                </>
              )}

              {selectedCategory && selectedCategory?.id !== "all" && (
                <GlossaryCategoryModal
                  glossary={glossary}
                  categories={categories}
                  isOpen={isEditCategoryModalOpen}
                  setSelectedCategory={setSelectedCategory}
                  onClose={onCategoryModalClose}
                  oldCategory={selectedCategory}
                />
              )}
            </Box>
          )}
        </Box>
        <AlphabeticFilter
          glossary={glossary}
          categories={categories}
          containerId={termsListId}
          terms={orderBy(terms, ["title"], ["asc"])}
          isPrintView={isPrintView}
        />
        <div ref={contentRef}>
          {terms.map((term) => (
            <GlossaryTermItem
              key={term.id}
              term={term}
              terms={terms}
              categories={categories}
              createGlossaryTermActions={createGlossaryTermActions}
              glossary={glossary}
              isPrintView={isPrintView}
            />
          ))}
        </div>

        {termToEdit && glossary && (
          <GlossaryTermFormModal
            showId={showId}
            glossary={glossary}
            terms={showGlossary.allTerms}
            categories={categories}
            onClose={() => setTermToEdit(undefined)}
            isOpen={!!termToEdit}
            editableTerm={termToEdit}
            onSave={onTryToSaveTerm}
            {...(libraryActions.isCompanyAdmin && {
              onAddToLibrary: async (term: ITerm) => {
                await libraryActions.pushToLibrary(term);
                setTermToEdit(undefined);
              },
              onUnlinkFromLibrary: async (term: ITerm) => {
                await libraryActions.unlinkTerm(term);
                setTermToEdit(undefined);
              },
              onRemove: async (term: ITerm) => {
                await removeTerm(term);
                setTermToEdit(undefined);
              },
            })}
          />
        )}
      </div>
    </>
  );
};

export default GlossaryTerms;
